import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Circle, Ruler } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Template-day checklist sheet — optional helper to record what was verified on site.
export const TemplateChecklistSheet = ({ jobId, code, onClose, onComplete }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api
      .get(`/jobs/${jobId}/template-checklist`)
      .then(async ({ data: d }) => {
        if (!alive) return;
        // Nothing to tick — complete instantly instead of showing an empty sheet.
        if (!d.items || d.items.length === 0) {
          await onComplete?.();
          onClose();
          return;
        }
        setData(d);
      })
      .catch(() => onClose());
    return () => {
      alive = false;
    };
    // run once on mount; onComplete is captured intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, onClose]);

  const save = async (ticked, note) => {
    const { data: d } = await api.put(`/jobs/${jobId}/template-checklist`, { ticked, note });
    setData(d);
    return d;
  };
  const toggle = async (item) => {
    const ticked = data.ticked.includes(item) ? data.ticked.filter((x) => x !== item) : [...data.ticked, item];
    setData({ ...data, ticked });
    try {
      await save(ticked, data.note || "");
    } catch {
      toast.error("Could not save checklist");
    }
  };
  const finish = async () => {
    setBusy(true);
    try {
      await save(data.ticked, data.note || "");
      await onComplete();
      onClose();
    } catch {
      toast.error("Could not mark done");
    }
    setBusy(false);
  };

  const remaining = data ? data.items.filter((i) => !data.ticked.includes(i)) : [];
  return (
    <div className="fixed inset-0 z-[200] flex items-end bg-black/70 backdrop-blur-sm" data-testid="template-checklist-sheet" onClick={onClose}>
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-3xl border-t border-zinc-800 bg-zinc-950 px-4 pb-24 pt-3" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-zinc-700" />
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-purple-300">
          <Ruler size={14} weight="bold" /> Template checklist · {code}
        </div>
        <p className="mt-1 text-xs text-zinc-500">Tick what you can — the checklist is optional.</p>
        {!data ? (
          <p className="mt-4 text-sm text-zinc-500">Loading…</p>
        ) : (
          <>
            <div className="mt-3 space-y-2">
              {data.items.map((item, i) => {
                const on = data.ticked.includes(item);
                return (
                  <button
                    key={item}
                    type="button"
                    data-testid={`template-check-${i}`}
                    onClick={() => toggle(item)}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left text-sm transition-colors ${
                      on ? "border-emerald-500/40 bg-emerald-500/10 text-zinc-100" : "border-zinc-800 bg-zinc-900 text-zinc-300"
                    }`}
                  >
                    {on ? <CheckCircle size={22} weight="fill" className="shrink-0 text-emerald-400" /> : <Circle size={22} className="shrink-0 text-zinc-600" />}
                    {item}
                  </button>
                );
              })}
            </div>
            <textarea
              data-testid="template-check-note"
              rows={2}
              value={data.note || ""}
              onChange={(e) => setData({ ...data, note: e.target.value })}
              onBlur={() => save(data.ticked, data.note || "").catch(() => {})}
              placeholder="Notes for fabrication (optional)…"
              className="mt-3 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-purple-400"
            />
            <button
              type="button"
              data-testid="template-check-done"
              disabled={busy}
              onClick={finish}
              className="mt-3 h-12 w-full rounded-xl bg-emerald-600 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-40"
            >
              {busy ? "Saving…" : remaining.length > 0 ? `Mark template done · ${remaining.length} left` : "Mark template done"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
